import { useCallback, useEffect, useRef, useState } from 'react';
import { ExtensionDetector } from '@/lib/extension-detector';

const EXTENSION_INSTALL_STARTED_KEY = 'cleanlist:extension-install-started';

export function markExtensionInstallStarted() {
  try {
    window.sessionStorage.setItem(EXTENSION_INSTALL_STARTED_KEY, 'true');
  } catch {
    // Storage can be unavailable in locked-down browsers. Detection still
    // works after a manual reload.
  }
}

export function consumeExtensionInstallStarted(): boolean {
  try {
    if (
      window.sessionStorage.getItem(EXTENSION_INSTALL_STARTED_KEY) !== 'true'
    ) {
      return false;
    }
    window.sessionStorage.removeItem(EXTENSION_INSTALL_STARTED_KEY);
    return true;
  } catch {
    return false;
  }
}

export interface ExtensionInstallationState {
  isInstalled: boolean;
  isReady: boolean;
  refresh: () => Promise<void>;
}

/**
 * Reports whether the extension is available in this browser. The check is
 * repeated when the user returns from the Chrome Web Store, so installing or
 * uninstalling the extension updates the navigation without persisting a
 * stale result across browsers.
 */
export function useExtensionInstalled(
  enabled = true
): ExtensionInstallationState {
  const [detected, setDetected] = useState<boolean | null>(null);
  const requestId = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const currentRequest = ++requestId.current;
    let installed = false;
    try {
      for (let attempt = 0; attempt < 2 && !installed; attempt += 1) {
        // The extension content script loads at document_idle and can miss a
        // handshake sent during the portal's first render.
        installed = await ExtensionDetector.isExtensionInstalled();
      }
    } catch {
      installed = false;
    }
    if (requestId.current === currentRequest) setDetected(installed);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      requestId.current += 1;
      setDetected(null);
      return;
    }

    let reloadScheduled = false;
    const reloadAfterInstall = () => {
      if (
        reloadScheduled ||
        document.visibilityState !== 'visible' ||
        !consumeExtensionInstallStarted()
      ) {
        return false;
      }
      reloadScheduled = true;
      window.location.reload();
      return true;
    };
    const checkWhenVisible = () => {
      if (reloadScheduled || document.visibilityState !== 'visible') return;
      if (reloadAfterInstall()) return;
      void refresh();
    };

    // The rollout can resolve after the user has already returned from the
    // Web Store. Consume that pending install immediately while visible.
    if (reloadAfterInstall()) return;

    void refresh();
    window.addEventListener('focus', checkWhenVisible);
    document.addEventListener('visibilitychange', checkWhenVisible);
    return () => {
      requestId.current += 1;
      window.removeEventListener('focus', checkWhenVisible);
      document.removeEventListener('visibilitychange', checkWhenVisible);
    };
  }, [enabled, refresh]);

  return {
    isInstalled: detected === true,
    isReady: detected !== null,
    refresh,
  };
}
